// third-party
import { presetPalettes, generate } from '@ant-design/colors';

// project imports
import ThemeOption from './theme';
import { extendPaletteWithChannels } from 'utils/colorUtils';
import { themeBackgrounds } from './curatedColors';

const greyAscent = ['#fafafa', '#bfbfbf', '#434343', '#1f1f1f'];

// ==============================|| GREY COLORS BUILDER ||============================== //

function buildGrey() {
  let greyPrimary = [
    '#ffffff',
    '#fafafa',
    '#f5f5f5',
    '#f0f0f0',
    '#d9d9d9',
    '#bfbfbf',
    '#8c8c8c',
    '#595959',
    '#262626',
    '#141414',
    '#000000'
  ];
  let greyConstant = ['#fafafb', '#e6ebf1'];

  return [...greyPrimary, ...greyAscent, ...greyConstant];
}

function buildDarkGrey() {
  let greyPrimary = [
    '#000000',
    '#141414',
    '#1e1e1e',
    '#262626',
    '#434343',
    '#595959',
    '#8c8c8c',
    '#bfbfbf',
    '#d9d9d9',
    '#f0f0f0',
    '#ffffff'
  ];
  let greyConstant = ['#121212', '#1a1a1a'];

  return [...greyPrimary, ...greyAscent, ...greyConstant];
}

// ==============================|| DEFAULT THEME - PALETTE ||============================== //

export function buildPalette(presetColor, customPrimaryColor, customSecondaryColor, darkModeStyle) {
  let lightColors = { ...presetPalettes, grey: buildGrey() };
  let darkColors = { ...presetPalettes, grey: buildDarkGrey() };

  if (customPrimaryColor) {
    try {
      const generatedLight = generate(customPrimaryColor);
      if (customSecondaryColor) {
        generatedLight[0] = customSecondaryColor;
      }
      lightColors.blue = generatedLight;

      const generatedDark = generate(customPrimaryColor, { theme: 'dark', backgroundColor: '#141414' });
      darkColors.blue = generatedDark;
    } catch (e) {
      console.error('Failed to generate custom palette:', e);
    }
  }

  const lightPaletteColor = ThemeOption(lightColors, presetColor);

  const commonColor = { common: { black: '#000', white: '#fff' } };

  const extendedLight = extendPaletteWithChannels(lightPaletteColor);
  const extendedCommon = extendPaletteWithChannels(commonColor);

  const darkPaletteColor = ThemeOption(darkColors, presetColor);
  const extendedDark = extendPaletteWithChannels(darkPaletteColor);

  // Setup Discord Dark Mode Theme Variables
  let darkBg = '#313338';
  let darkPaper = '#2b2d31';
  let sidebarBg = '#1e1f22';
  let primaryBlurple = '#5865f2';
  let textPrimary = '#f2f3f5';
  let textSecondary = '#949ba4';
  let dividerColor = '#3f4147';

  if (darkModeStyle === 'discord-midnight') {
    darkBg = '#000000';
    darkPaper = '#111214';
    sidebarBg = '#000000';
    primaryBlurple = '#5865f2';
    textPrimary = '#f2f3f5';
    textSecondary = '#949ba4';
    dividerColor = '#1c1d1f';
  } else if (darkModeStyle === 'discord-classic') {
    darkBg = '#36393f';
    darkPaper = '#2f3136';
    sidebarBg = '#202225';
    primaryBlurple = '#7289da';
    textPrimary = '#ffffff';
    textSecondary = '#b9bbbe';
    dividerColor = '#42464d';
  } else if (darkModeStyle === 'discord-glass') {
    darkBg = '#0a0d14';
    darkPaper = '#14182173'; // 8-digit hex equivalent of rgba(20, 24, 33, 0.45)
    sidebarBg = 'rgba(10, 12, 18, 0.6)';
    primaryBlurple = '#8a2be2';
    textPrimary = '#f2f3f5';
    textSecondary = '#a0aec0';
    dividerColor = 'rgba(255, 255, 255, 0.08)';
  } else {
    // discord-dark (default)
    darkBg = '#313338';
    darkPaper = '#2b2d31';
    sidebarBg = '#1e1f22';
    primaryBlurple = '#5865f2';
    textPrimary = '#f2f3f5';
    textSecondary = '#949ba4';
    dividerColor = '#3f4147';
  }

  // Dynamic theme-tinted backgrounds lookup for light mode
  let bgConfig = themeBackgrounds[presetColor];
  if (!bgConfig) {
    switch (presetColor) {
      case 'cyan':
        bgConfig = themeBackgrounds['ocean-teal'];
        break;
      case 'purple':
        bgConfig = themeBackgrounds['royal-indigo'];
        break;
      case 'green':
        bgConfig = themeBackgrounds['mint-green'];
        break;
      case 'red':
        bgConfig = themeBackgrounds['rose-coral'];
        break;
      case 'orange':
        bgConfig = themeBackgrounds['sunset-orange'];
        break;
      case 'default':
      case 'blue':
      default:
        bgConfig = themeBackgrounds['ocean-teal'];
    }
  }

  const activeLightPalette = {
    mode: 'light',
    ...extendedCommon,
    ...extendedLight,
    text: {
      primary: extendedLight.grey[700],
      secondary: extendedLight.grey[500],
      disabled: extendedLight.grey[400]
    },
    action: { disabled: extendedLight.grey[300] },
    divider: extendedLight.grey[200],
    background: {
      paper: bgConfig.lightPaper,
      default: bgConfig.lightBg,
      sidebar: '#ffffff'
    }
  };

  const activeDarkPalette = {
    mode: 'dark',
    ...extendedCommon,
    ...extendedDark,
    primary: {
      ...extendedDark.primary,
      main: primaryBlurple,
      light: primaryBlurple + '30',
      lighter: primaryBlurple + '15',
      dark: primaryBlurple,
      darker: primaryBlurple
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary,
      disabled: '#72767d'
    },
    action: { disabled: '#4f545c' },
    divider: dividerColor,
    background: {
      paper: darkPaper,
      default: darkBg,
      sidebar: sidebarBg
    }
  };

  return {
    light: activeLightPalette,
    dark: activeDarkPalette
  };
}
